import React, { useState, useEffect } from 'react';
import { User, Mail, Calendar, Edit3, Save, X, CheckCircle, Users, Shield, KeyRound, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import IconButton from '../components/IconButton';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  // Carregar todos os usuários se for admin
  useEffect(() => {
    const loadAllUsers = async () => {
      if (isAdmin && activeTab === 'users') {
        setIsLoadingUsers(true);
        try {
          const response = await userService.getUsers();
          if (response.success) {
            setAllUsers(response.users || []);
          } else {
            toast.error('Erro ao carregar usuários');
          }
        } catch (error) {
          console.error('Erro ao carregar usuários:', error);
          toast.error('Erro ao carregar usuários');
        } finally {
          setIsLoadingUsers(false);
        }
      }
    };

    loadAllUsers();
  }, [isAdmin, activeTab]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = () => {
    setIsEditing(true);
    setErrors({});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: user.name || '',
      email: user.email || ''
    });
    setErrors({});
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    
    try {
      const response = await userService.updateProfile({
        name: formData.name.trim(),
        email: formData.email.trim()
      });
      
      if (response.success) {
        // Atualizar dados do usuário no contexto
        updateUser({
          ...user,
          name: formData.name.trim(),
          email: formData.email.trim()
        });
        
        setIsEditing(false);
        toast.success('Perfil atualizado com sucesso!');
      } else {
        if (response.message?.includes('E-mail já está em uso')) {
          setErrors({ email: 'Este e-mail já está sendo usado por outro usuário' });
        } else {
          setErrors({ general: response.message || 'Erro ao atualizar perfil' });
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      const message = error.response?.data?.message || 'Erro interno do servidor';
      
      if (message.includes('E-mail já está em uso')) {
        setErrors({ email: 'Este e-mail já está sendo usado por outro usuário' });
      } else {
        setErrors({ general: message });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não informado';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data inválida';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'user':
        return 'Usuário';
      default:
        return 'Não definido';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'user':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Meu Perfil</h1>
        <p className="text-gray-600 mt-1">
          {isAdmin ? 'Gerencie suas informações e visualize todos os usuários do sistema.' : 'Gerencie suas informações pessoais e configurações da conta.'}
        </p>
      </div>

      {/* Tabs para Admin */}
      {isAdmin && (
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <IconButton
                icon={User}
                onClick={() => setActiveTab('profile')}
                variant={activeTab === 'profile' ? 'primary' : 'secondary'}
                title="Meu Perfil"
              />
              <IconButton
                icon={Users}
                onClick={() => setActiveTab('users')}
                variant={activeTab === 'users' ? 'primary' : 'secondary'}
                title="Todos os Usuários"
              />
            </nav>
          </div>
        </div>
      )}

      {/* Conteúdo baseado na aba ativa */}
      {(!isAdmin || activeTab === 'profile') && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Card do perfil */}
          <div className="lg:col-span-2">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Informações Pessoais</h2>
              {!isEditing && (
                <IconButton
                  icon={Edit3}
                  onClick={handleEdit}
                  variant="warning"
                  title="Editar perfil"
                />
              )}
            </div>

            {/* Erro geral */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-6">
                {errors.general}
              </div>
            )}

            <div className="space-y-6">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome completo
                </label>
                {isEditing ? (
                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`input pl-10 ${errors.name ? 'border-red-300 focus:ring-red-500' : ''}`}
                        placeholder="Digite seu nome completo"
                      />
                    </div>
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <User className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-gray-900">{user?.name || 'Não informado'}</span>
                  </div>
                )}
              </div>

              {/* E-mail */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail
                </label>
                {isEditing ? (
                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`input pl-10 ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                        placeholder="Digite seu e-mail"
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <Mail className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-gray-900">{user?.email || 'Não informado'}</span>
                  </div>
                )}
              </div>

              {/* Botões de ação */}
              {isEditing && (
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <IconButton
                    icon={X}
                    onClick={handleCancel}
                    disabled={isSaving}
                    variant="secondary"
                    title="Cancelar"
                  />
                  <IconButton
                    icon={Save}
                    onClick={handleSave}
                    disabled={isSaving}
                    variant="primary"
                    title={isSaving ? 'Salvando...' : 'Salvar'}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar com informações adicionais */}
        <div className="space-y-6">
          {/* Card de status da conta */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status da Conta</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tipo de usuário</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>
                  {getRoleLabel(user?.role)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">Ativo</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card de informações da conta */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações da Conta</h3>
            <div className="space-y-4">
              <div>
                <span className="text-sm text-gray-600 block">Membro desde</span>
                <div className="flex items-center mt-1">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">
                    {formatDate(user?.createdAt)}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600 block">Última atualização</span>
                <div className="flex items-center mt-1">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">
                    {formatDate(user?.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Card de ações rápidas */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
            <div className="space-y-3">
              <IconButton
                icon={KeyRound}
                onClick={() => navigate('/change-password')}
                variant="secondary"
                title="Alterar senha"
              />
              <IconButton
                icon={Settings}
                onClick={() => navigate('/configuracoes')}
                variant="secondary"
                title="Configurações"
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Aba de Todos os Usuários (apenas para Admin) */}
      {isAdmin && activeTab === 'users' && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Shield className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Todos os Usuários do Sistema</h2>
              </div>
              <span className="text-sm text-gray-500">
                {allUsers.length} usuário{allUsers.length !== 1 ? 's' : ''} encontrado{allUsers.length !== 1 ? 's' : ''}
              </span>
            </div>

            {isLoadingUsers ? (
              <div className="flex justify-center items-center h-32">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <div className="space-y-4">
                {allUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhum usuário encontrado</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allUsers.map((userItem) => (
                      <div key={userItem.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{userItem.name}</h3>
                              <p className="text-sm text-gray-500">{userItem.email}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(userItem.role)}`}>
                            {getRoleLabel(userItem.role)}
                          </span>
                        </div>
                        
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Criado em: {formatDate(userItem.createdAt)}</span>
                          </div>
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                            <span className="text-green-600">Ativo</span>
                          </div>
                        </div>
                        
                        {userItem.id === user.id && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <User className="w-3 h-3 mr-1" />
                              Você
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;